import { useEffect, useState } from 'react'
import Swal from 'sweetalert2'

const BUILD_TIME = process.env.REACT_APP_BUILD_TIME
const isDev = process.env.NODE_ENV === 'development'

let staleDetected = false

async function fetchServerBuildTime () {
  const res = await fetch('/version.json?_=' + Date.now())
  const { buildTime } = await res.json()
  return buildTime
}

// Blocking confirm dialog used before form submission.
// The user must reload — there is no dismiss path.
async function showSubmitBlocker () {
  await Swal.fire({
    title: 'Update Required',
    text: 'A newer version of this application is available. Please reload before submitting.',
    icon: 'warning',
    confirmButtonText: 'Reload Now',
    allowOutsideClick: false,
    allowEscapeKey: false,
  })
  window.location.reload()
}

// Call this before any form submission.
// Returns false (and prompts) if the client is stale; returns true when safe to proceed.
export async function assertLatestVersion () {
  if (isDev) return true
  if (staleDetected) {
    await showSubmitBlocker()
    return false
  }
  try {
    const serverBuildTime = await fetchServerBuildTime()
    if (serverBuildTime !== BUILD_TIME) {
      staleDetected = true
      await showSubmitBlocker()
      return false
    }
  } catch {
    return true // fail open — allow submission when version check is unreachable
  }
  return true
}

// Persistent top-right banner shown when the background poll detects a new version.
function UpdateBanner () {
  return (
    <div style={{
      position: 'fixed',
      top: 16,
      right: 16,
      zIndex: 99999,
      background: '#5e4e7a',
      color: '#fff',
      padding: '12px 16px',
      borderRadius: 8,
      boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      maxWidth: 340,
      fontFamily: 'inherit',
      fontSize: 14,
      lineHeight: '1.4',
    }}>
      <span style={{ flex: 1 }}>
        A newer version of this application is available. Please reload the page.
      </span>
      <button
        onClick={() => window.location.reload()}
        style={{
          background: '#fff',
          color: 'rgb(30 175 155)',
          border: 'none',
          borderRadius: 5,
          padding: '6px 12px',
          cursor: 'pointer',
          fontWeight: 700,
          fontSize: 13,
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}
      >
        Reload
      </button>
    </div>
  )
}

// Add to <App> alongside <Session>. Polls on the given interval and shows a
// non-blocking banner in the top-right corner when a new version is detected.
export function VersionWatcher ({ interval }) {
  const [updateAvailable, setUpdateAvailable] = useState(false)

  useEffect(() => {
    if (isDev) return

    async function check () {
      try {
        const serverBuildTime = await fetchServerBuildTime()
        if (serverBuildTime !== BUILD_TIME) {
          staleDetected = true
          setUpdateAvailable(true)
        }
      } catch {
        // network error — retry on next poll
      }
    }

    const id = setInterval(check, interval)
    return () => clearInterval(id)
  }, [interval])

  return updateAvailable ? <UpdateBanner /> : null
  // return  <UpdateBanner />
}
